import { useState } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { useProductName } from '../../../../lib/use-brand';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useNarrow } from '../../../../lib/use-narrow';
import { inviteCardCopy, type InviteCardData } from '../../../../lib/invite-card';
import { useShareLink } from '../../../settings/shared/use-share-app';
import { ShareAppModal } from '../../../settings/shared/ShareAppModal';
import { Button, Card, Toast, Typography } from '../../ui';
import styles from './ShareAskCard.module.css';

/**
 * "Know someone who'd like this?" — the owner's own invite link, under a reply the server judged a good
 * moment (`metadata.inviteCard`). A sibling of `CreditsCard`, riding its reply the same way: the answer
 * stays, the ask sits under it and costs nothing to scroll past.
 *
 * The button shares the link the CARD carries — the attributed one the Share screen shows — through the
 * same share action the Share screen uses (`navigator.share`, falling back to the clipboard with a "Link
 * copied" toast). "See your invites" opens that Share screen itself.
 *
 * `earn` names the served referrer reward (all three parts, or none — `inviteCardCopy`); `recommend` is
 * the account's once-ever ask and carries no reward wording.
 *
 * Shared 1:1 with the phones' invite cards.
 */
export function InviteCard({ card }: { card: InviteCardData }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const narrow = useNarrow();
  const { logPageClickBi } = useGenericBi();
  // The card reports its own click below; the hook's Settings `share_app` click would miscount this one.
  const share = useShareLink(card.link, null);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const biFields = { variant: card.variant };
  // The impression (198:6), attached to the card so it fires when the card is scrolled into view.
  const seenRef = useItemSeenBi('invite-card', { additionalFields: biFields });
  const click = (button: string) => {
    logPageClickBi({ page_name: 'chat', button_name: button }, { componentName: button, additionalFields: biFields });
  };

  const copy = inviteCardCopy(card);
  const title = t(copy.titleKey, { productName });

  const onShare = async () => {
    click('invite_card_share');
    const outcome = await share();
    if (outcome === 'copied') setToast({ message: t('packs.linkCopied'), severity: 'success' });
    else if (outcome === 'failed') setToast({ message: t('packs.actionFailed'), severity: 'error' });
  };

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={title} data-testid="chat-invite-card" data-variant={card.variant}>
      <Typography variant="h4" component="h3">
        {title}
      </Typography>
      <Typography variant="p2" component="p" color="secondary">
        {t(copy.bodyKey, { productName, ...copy.bodyParams })}
      </Typography>
      <div className={styles.actions}>
        <Button fullWidth onClick={() => void onShare()}>
          {t('chat.inviteCard.share')}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={() => {
            click('invite_card_see_invites');
            setInvitesOpen(true);
          }}
        >
          {t('chat.inviteCard.seeInvites')}
        </Button>
      </div>
      <ShareAppModal open={invitesOpen} onClose={() => setInvitesOpen(false)} variant={narrow ? 'mobile' : 'desktop'} />
      <Toast open={toast !== null} message={toast?.message ?? ''} severity={toast?.severity} onClose={() => setToast(null)} />
    </Card>
  );
}
